<template>
<HelpElement
	:pathname
	:tutorials
	:navbarDisplayed="ScreenSize <= 1"
/>

<VideoTrigger
	v-if="globalTutorialVideo.source"
	:source="globalTutorialVideo.source"
	:videos="globalTutorialVideo.videos"
	:hideTrigger="true"
/>

<RecomendationOnboardingDialog v-if="apDialogIsVisible"/>

<div v-if="CurrentState.GlobalDialog">
	<UnusedCatsDialog v-if="CurrentState.GlobalDialog === 'UnusedCats'"/>
	<TheBestSurveyDialog v-if="CurrentState.GlobalDialog === 'TheBestSurveyDialog'"/>
	<PushNotificationsDialog v-if="CurrentState.GlobalDialog === 'PushNotificationsDialog'"/>
	<OnboardingIntroDialog v-if="CurrentState.GlobalDialog === 'OnboardingIntroDialog'"/>
	<OnboardingAfterDialog v-if="CurrentState.GlobalDialog === 'OnboardingAfterDialog'"/>
	<FakedoorTestDialog v-if="CurrentState.GlobalDialog === 'FakedoorTestDialog'"/>
	<AiDescriptionDialog v-if="CurrentState.GlobalDialog === 'AiDescriptionDialog'"/>
	<AppRateDialog v-if="CurrentState.GlobalDialog === 'AppRateDialog'"/>
	<PromotionModal v-if="CurrentState.GlobalDialog === 'PromotionModal'"/>
</div>

<div data-app-type="customer"/>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import emitter from 'tiny-emitter/instance';
import { defineAsyncComponent, onMounted, watch, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';

import jsonFetch from '@globals/jsonFetch';
import type StateManager from '@globals/StateManager';
import { useAppStore } from '@globals/stores/app';
import { useUserStore } from '@globals/stores/user';

import HelpElement from '@customer/components/HelpElement.vue';
import VideoTrigger from '@customer/components/VideoTrigger.vue';
import { useFingerprint } from '@customer/composables/fingerprint';
import { useAccountRechargeStore } from '@customer/stores/accountRecharge';
import { useCustomerOnboardingStore } from '@customer/stores/customerOnboarding';
import { useCustomerProfileViewStore } from '@customer/stores/customerProfileView';
import { usePromotionStore } from '@customer/stores/promotion';
import { useServicesStore } from '@customer/stores/services';

const router = useRouter();

const appStore = useAppStore();
const customerOnboardingStore = useCustomerOnboardingStore();
const customerProfileViewStore = useCustomerProfileViewStore();
const promotionStore = usePromotionStore();
const servicesStore = useServicesStore();

const { pathname, isVirtualKeyboardVisible } = storeToRefs(appStore);
const { globalTutorialVideo, tutorials } = storeToRefs(customerOnboardingStore);
const { isVueRouterView } = storeToRefs(customerProfileViewStore);

const RecomendationOnboardingDialog = defineAsyncComponent(() => import('./dialog/RecomendationOnboarding/RecomendationOnboardingDialog.vue'));

// Asynchroniczne modale
// TODO: użyć <component :is=""/>
// TODO: zrobić tak, by działało na stronach z Vue Routerem
const UnusedCatsDialog = defineAsyncComponent(() => import('./dialog/UnusedCatsDialog.vue'));
const TheBestSurveyDialog = defineAsyncComponent(() => import('./dialog/TheBestSurveyDialog.vue'));
const PushNotificationsDialog = defineAsyncComponent(() => import('./dialog/PushNotificationsDialog.vue'));
const OnboardingIntroDialog = defineAsyncComponent(() => import('./onboarding/OnboardingIntroDialog.vue'));
const OnboardingAfterDialog = defineAsyncComponent(() => import('./onboarding/OnboardingAfterDialog.vue'));
const FakedoorTestDialog = defineAsyncComponent(() => import('./dialog/FakedoorTestDialog.vue'));
const AiDescriptionDialog = defineAsyncComponent(() => import('./dialog/AiDescriptionDialog.vue'));
const AppRateDialog = defineAsyncComponent(() => import('./dialog/AppRateDialog.vue'));
const PromotionModal = defineAsyncComponent(() => import('./profile/promotion/discount2in1/PromotionModal.vue'));

const modals = {
	onboardingIntro: 'OnboardingIntroDialog',
	onboardingAfter: 'OnboardingAfterDialog',
	fakedoorTest: 'FakedoorTestDialog',
	aiDescription: 'AiDescriptionDialog',
	postPayment: 'PostPayment',
	promoPromotion2in1: 'PromotionModal',
	pushNotifModal: 'PushNotificationsDialog',
	googleRate: 'AppRateDialog',
	appRate: 'AppRateDialog',
	theBestSurvey: 'TheBestSurveyDialog',
} as Record<string, string>;

/**
 * Wyświetla okno dialogowe wyświetlające się automatycznie na danym URL-u
 */
function updateGlobalModals(pathname: string, old: string) {
	try {
		if (pathname === old) { return; }
		const modalToShow = customerOnboardingStore.canShowModal(Object.keys(modals), pathname);
		const globalDialogName = modals[modalToShow] || '';
		if (!globalDialogName) { return; }

		setTimeout(() => {
			if (isVueRouterView.value) {
				router.push({ query: { globalDialog: globalDialogName } });
			} else if (props.state) {
				props.state.Append({ GlobalDialog: globalDialogName });
			}
		}, 500);
	} catch (err) {
		console.warn(err);
	}
}

/**
 * Dodaje obsługę zdarzenia otwarcia klawiatury wirtualnej na urządzeniach mobilnych,
 * poprzez sprawdzenie różnicy między wysokością okna a `visualViewport`.
 */
function addVirtualKeyboardListener() {
	// To byłby fajny sposób - blokuje możliwość zmiany wysokości strony przy pokazaniu klawiatury.
	// Niestety ma dwa problemy: nie ma wsparcia na iOS oraz nie przewija strony do inputa z fokusem.
	// navigator.virtualKeyboard.overlaysContent = true;

	if (window.visualViewport) {
		window.visualViewport.addEventListener('resize', () => {
			isVirtualKeyboardVisible.value = (window.visualViewport?.height || Number.MAX_VALUE) < window.innerHeight;
		});
	}
};

/**
 * Resetuje state Pinii gdy nastąpi przekierowanie z StateManager -> VueRouter
 */
function resetPiniaStoreValues() {
	appStore.nextRouteNameAttempt = '';
	customerProfileViewStore.isFormChanged = false;
	promotionStore.isSummaryPage = false;
	servicesStore.selectedCategoryGroupId = 0;
	servicesStore.currentPage = '';
};

/**
 * Przesyłanie fingerprintu przeglądarki do servera w celu weryfikacji
 * czy ktoś nie cwaniakuje na promocjach
 */
async function sendFingerprintToServer() {
	const { get } = await useFingerprint();
	const fingerprint = await get() || '';
	try {
		await jsonFetch('/myoferteo/konto-json/save-fingerprint.do', {
			method: 'POST',
			query: { fingerprint },
		});
	} catch (error) {
		console.warn('Error sending fingerprint to server:', error);
	}
};

const props = defineProps<{
	state?: StateManager;
}>();

const apDialogIsVisible = computed(() => {
	/** TODO: przerobić na router?.currentRoute?.value?.query?.globalDialog === 'APOnboardingDialog' jak się dowiem czemu nie widzi globalDialog w query */
	const queryParam = window.location.search.includes('globalDialog=APOnboardingDialog');
	return queryParam || props?.state?.CurrentState?.state?.GlobalDialog === 'APOnboardingDialog';
});

onMounted(() => {
	addVirtualKeyboardListener();
	sendFingerprintToServer();

	customerOnboardingStore.getModalList().then(() => {
		watch(pathname, (val: string, old: string) => {
			updateGlobalModals(val, old);
		});
		updateGlobalModals(pathname.value, '');
	});

	watch(isVueRouterView, (val: boolean) => {
		if (val) { resetPiniaStoreValues(); }
	});
});

emitter.on('balance', (balanceData) => {
	const userStore = useUserStore();
	const accountRecharge = useAccountRechargeStore();
	accountRecharge.paymentProgress = (userStore.accountBalance < balanceData.accountBalance) ? 'success' : 'fail';
	userStore.paymentProgress = (userStore.accountBalance < balanceData.accountBalance) ? 'success' : 'fail';
	userStore.accountBalance = balanceData.accountBalance;
	userStore.onboardingFreeUncoversLeft = balanceData.onboardingFreeUncoversLeft;
});
</script>
